'use client'

import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Envelope, MapPin, LinkedinLogo, PaperPlaneTilt, CheckCircle, Spinner } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'
import { SCHEDULE_CALL_URL, hasScheduleCallUrl } from '@/config/site'

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  })
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [submitCount, setSubmitCount] = useState(0)
  const [lastSubmitTime, setLastSubmitTime] = useState(0)
  const [honeypot, setHoneypot] = useState('')

  // Security validation functions
  const validateInput = (input: string, type: 'name' | 'email' | 'company' | 'message'): boolean => {
    if (!input || input.trim().length === 0) return false
    
    // Basic length limits
    const limits = {
      name: 100,
      email: 254,
      company: 200,
      message: 2000
    }
    
    if (input.length > limits[type]) return false
    
    // Check for suspicious patterns
    const suspiciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /data:text\/html/i,
      /vbscript:/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /<link/i,
      /<meta/i,
      /<style/i,
      /expression\s*\(/i,
      /url\s*\(/i,
      /@import/i,
      /eval\s*\(/i,
      /document\./i,
      /window\./i,
      /alert\s*\(/i,
      /confirm\s*\(/i,
      /prompt\s*\(/i
    ]
    
    return !suspiciousPatterns.some(pattern => pattern.test(input))
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email) && email.length <= 254
  }

  const sanitizeInput = (input: string): string => {
    return input
      .trim()
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .substring(0, 2000) // Limit length
  }

  const checkRateLimit = (): boolean => {
    const now = Date.now()
    const timeSinceLastSubmit = now - lastSubmitTime
    
    // Allow max 3 submissions per minute
    if (timeSinceLastSubmit < 60000 && submitCount >= 3) {
      return false
    }
    
    // Reset counter if more than a minute has passed
    if (timeSinceLastSubmit >= 60000) {
      setSubmitCount(0)
    }
    
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      // Security checks
      if (honeypot) {
        // Honeypot field should be empty - this is likely a bot
        console.warn('Honeypot triggered - potential bot submission')
        setError('Invalid submission detected.')
        setIsLoading(false)
        return
      }

      if (!checkRateLimit()) {
        setError('Too many submissions. Please wait a moment before trying again.')
        setIsLoading(false)
        return
      }

      // Validate required fields
      if (!validateInput(formData.name, 'name')) {
        setError('Please enter a valid name (max 100 characters).')
        setIsLoading(false)
        return
      }

      if (!validateEmail(formData.email)) {
        setError('Please enter a valid email address.')
        setIsLoading(false)
        return
      }

      if (!validateInput(formData.message, 'message')) {
        setError('Please enter a valid message (max 2000 characters).')
        setIsLoading(false)
        return
      }

      // Validate optional company field
      if (formData.company && !validateInput(formData.company, 'company')) {
        setError('Company name contains invalid characters.')
        setIsLoading(false)
        return
      }

      // Sanitize inputs
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        company: sanitizeInput(formData.company),
        message: sanitizeInput(formData.message)
      }

      // Update rate limiting
      setSubmitCount(prev => prev + 1)
      setLastSubmitTime(Date.now())

      // Using Formspree to send emails
      const formDataToSend = new FormData()
      formDataToSend.append('name', sanitizedData.name)
      formDataToSend.append('email', sanitizedData.email)
      formDataToSend.append('company', sanitizedData.company)
      formDataToSend.append('message', sanitizedData.message)
      formDataToSend.append('_subject', `New Contact Form Submission from ${sanitizedData.name}`)
      formDataToSend.append('_replyto', sanitizedData.email)

      // Replace 'YOUR_FORM_ID' with your actual Formspree form ID
      // To set up Formspree:
      // 1. Go to https://formspree.io and sign up
      // 2. Create a new form
      // 3. Copy the form ID from the integration code
      // 4. Replace 'YOUR_FORM_ID' below with your actual form ID
      const response = await fetch('https://formspree.io/f/xeorldnn', {
        method: 'POST',
        body: formDataToSend,
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        setIsSubmitted(true)
        setFormData({ name: '', email: '', company: '', message: '' })
        setTimeout(() => setIsSubmitted(false), 5000)
      } else {
        throw new Error('Failed to send message')
      }

    } catch (err) {
      setError('Failed to send message. Please try again or contact me directly at dixon.bordiano@proton.me')
      console.error('Email sending error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <div className="min-h-screen relative overflow-hidden transition-none">
      
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-[var(--brutal-ink)] mb-6 leading-tight">
              Get In Touch
            </h1>
            <p className="text-lg md:text-xl text-[var(--brutal-muted)] max-w-4xl mx-auto leading-relaxed">
              Ready to transform your enterprise challenges into exceptional user experiences? Let's discuss your project.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <Card className="brutal-panel p-8">
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">Let's Work Together</h2>
                <div className="space-y-6 text-[var(--brutal-muted)] leading-relaxed">
                  <p>
                    I'm always excited to collaborate on challenging projects that push the boundaries of enterprise UX design. Whether you're looking to transform an existing product or build something entirely new, I'd love to hear about your vision.
                  </p>
                  <p>
                    My approach focuses on understanding your unique business challenges, user needs, and technical constraints to deliver solutions that drive measurable results.
                  </p>
                </div>
                
                <div className="mt-8 space-y-4">
                  <div className="flex items-center space-x-4 text-[var(--brutal-muted)]">
                    <div className="w-10 h-10 bg-[var(--brutal-panel)] rounded-full flex items-center justify-center">
                      <Envelope weight="duotone" className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-[var(--brutal-ink)]">Email</p>
                      <a href="mailto:dbordiano@gmail.com" className="text-sm hover:underline">dbordiano@gmail.com</a>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-[var(--brutal-muted)]">
                    <div className="w-10 h-10 bg-[var(--brutal-panel)] rounded-full flex items-center justify-center">
                      <MapPin weight="duotone" className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-[var(--brutal-ink)]">Location</p>
                      <p className="text-sm">Porto, Portugal</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-[var(--brutal-muted)]">
                    <div className="w-10 h-10 bg-[var(--brutal-panel)] rounded-full flex items-center justify-center">
                      <LinkedinLogo weight="duotone" className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-[var(--brutal-ink)]">LinkedIn</p>
                      <a href="https://www.linkedin.com/in/dixonbordiano/" target="_blank" rel="noopener noreferrer" className="text-sm hover:underline">linkedin.com/in/dixonbordiano</a>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            
            <div>
              <Card className="brutal-panel p-8">
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">Send a Message</h2>
                
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <CheckCircle weight="duotone" className="w-16 h-16 text-[var(--brutal-success)] mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-2">Message Sent!</h3>
                    <p className="text-[var(--brutal-muted)]">Thank you for reaching out. I'll get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field - hidden from users but visible to bots */}
                    <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                      <label htmlFor="website">Website (leave blank):</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-[var(--brutal-ink)] mb-2">
                          Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          maxLength={100}
                          value={formData.name}
                          onChange={handleChange}
                          className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] placeholder:text-[var(--brutal-muted)] focus:border-cyan-400 focus:ring-cyan-400"
                          placeholder="Your full name"
                        />
                        <div className="text-xs text-[var(--brutal-muted)] mt-1">
                          {formData.name.length}/100 characters
                        </div>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-[var(--brutal-ink)] mb-2">
                          Email *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          maxLength={254}
                          value={formData.email}
                          onChange={handleChange}
                          className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] placeholder:text-[var(--brutal-muted)] focus:border-cyan-400 focus:ring-cyan-400"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-[var(--brutal-ink)] mb-2">
                        Company
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        maxLength={200}
                        value={formData.company}
                        onChange={handleChange}
                        className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] placeholder:text-[var(--brutal-muted)] focus:border-cyan-400 focus:ring-cyan-400"
                        placeholder="Your company name"
                      />
                      <div className="text-xs text-[var(--brutal-muted)] mt-1">
                        {formData.company.length}/200 characters
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[var(--brutal-ink)] mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        maxLength={2000}
                        value={formData.message}
                        onChange={handleChange}
                        className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] placeholder:text-[var(--brutal-muted)] focus:border-cyan-400 focus:ring-cyan-400 resize-none"
                        placeholder="Tell me about your project, challenges, or how I can help..."
                      />
                      <div className="text-xs text-[var(--brutal-muted)] mt-1">
                        {formData.message.length}/2000 characters
                      </div>
                    </div>
                    
                    {error && (
                      <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 text-[var(--brutal-accent)] text-sm">
                        {error}
                      </div>
                    )}
                    
                    <Button 
                      type="submit"
                      disabled={isLoading}
                      className="w-full brutal-btn text-[var(--brutal-ink)] border-0 py-3 text-lg font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {isLoading ? (
                        <>
                          <Spinner weight="duotone" className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <PaperPlaneTilt weight="duotone" className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                    
                    <p className="text-[var(--brutal-muted)] text-sm text-center">
                      I typically respond within 24 hours. For urgent inquiries, feel free to reach out directly via email.
                    </p>
                  </form>
                )}
              </Card>
            </div>
          </div>
          
          {/* What I Can Help With - Full Width Section */}
          <Card className="brutal-panel p-8 mt-12">
            <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6">What I Can Help With</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                <div>
                  <h4 className="text-[var(--brutal-ink)] font-semibold">Enterprise UX Strategy</h4>
                  <p className="text-[var(--brutal-muted)] text-sm">Strategic design planning for complex B2B platforms and enterprise software</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                <div>
                  <h4 className="text-[var(--brutal-ink)] font-semibold">Design Leadership</h4>
                  <p className="text-[var(--brutal-muted)] text-sm">Building and scaling design teams, establishing design culture and processes</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                <div>
                  <h4 className="text-[var(--brutal-ink)] font-semibold">Product Transformation</h4>
                  <p className="text-[var(--brutal-muted)] text-sm">Redesigning existing products for better user experience and business outcomes</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                <div>
                  <h4 className="text-[var(--brutal-ink)] font-semibold">Design Systems</h4>
                  <p className="text-[var(--brutal-muted)] text-sm">Creating scalable design systems and component libraries for enterprise applications</p>
                </div>
              </div>
            </div>
          </Card>
          
          <Card className="brutal-panel p-8 text-center mt-16">
            <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4">Ready to Start Your Project?</h3>
            <p className="text-[var(--brutal-muted)] mb-6 max-w-2xl mx-auto">
              Whether you're looking to improve an existing product or create something entirely new, I'm here to help transform your vision into reality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                variant="outline"
                className="brutal-btn-ghost"
              >
                <Link to="/ProfessionalProjects">View My Work</Link>
              </Button>
              {hasScheduleCallUrl ? (
                <Button asChild className="brutal-btn">
                  <a
                    href={SCHEDULE_CALL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Schedule a Call
                  </a>
                </Button>
              ) : (
                <Button asChild className="brutal-btn">
                  <a href="mailto:dbordiano@gmail.com?subject=Schedule%20a%20call">
                    Schedule a Call
                  </a>
                </Button>
              )}
            </div>
          </Card>
        </div>
      </main>
    </div>
  )
}